/**
 * ParticipantPicker — M-Rhythm-3 (F-MUT1 Packet B AC-13 redesign)
 *
 * F-MUT2 changes:
 *   AC-MUT2-1: Role pill removed — no attendee/observer UI; role defaults 'attendee'.
 *   AC-MUT2-2: Click/pointer-down on input reopens dropdown after a selection.
 *   AC-MUT2-5: Participant rows + dropdown options show the correct type icon
 *              (human/ai_operator/knowledge_persona/domain) — mirrors Actors-page mapping.
 *
 * Compliance: M-Rhythm-3 plan §5 task 6 — no raw <button> or <input>.
 */

import React, { useState, useRef, useEffect } from 'react';
import { User, Building2, X, Bot, BookOpen } from 'lucide-react';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { ParticipantKind, ParticipantRole } from '@/types/roros/meetings';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface ParticipantEntry {
  /** Stable client-side key (not DB id) */
  key: string;
  participantKind: ParticipantKind;
  actorId: string | null;
  domainId: string | null;
  role: ParticipantRole;
  /** Resolved display name for display purposes */
  displayName: string | null;
}

export interface ParticipantPickerProps {
  /** Current list of participants */
  participants: ParticipantEntry[];
  onChange: (participants: ParticipantEntry[]) => void;
  /** All actors available for selection */
  actors: RorosActorDTO[];
  /** All domains available for domain-accountable selection */
  domains?: RorosDomainDTO[];
  disabled?: boolean;
  /** When true, shows loading indicators for participant display names. */
  loading?: boolean;
  /** Optional controlled search value. When omitted, the picker keeps local search state. */
  searchValue?: string;
  /** Optional controlled search setter, used by callers that search actors server-side. */
  onSearchChange?: (value: string) => void;
  /** Shows a loading row while server-side search is refreshing. */
  isSearching?: boolean;
  className?: string;
  'data-testid'?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

let _keyCounter = 0;
function nextKey(): string {
  return `participant-${++_keyCounter}`;
}

type PickerOption = {
  kind: 'actor' | 'domain';
  id: string;
  name: string;
  /** Actor sub-type (human/ai_operator/knowledge_persona) — undefined for domains */
  actorType?: string;
};

/**
 * AC-MUT2-5 — Icon for a participant/option, mirroring Actors-page actorTypeBadge mapping.
 * human → User, ai_operator → Bot, knowledge_persona → BookOpen, domain → Building2.
 */
function ActorTypeIcon({ actorType, isDomain, className }: {
  actorType?: string;
  isDomain?: boolean;
  className?: string;
}): React.JSX.Element {
  if (isDomain) return <Building2 className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground', className)} />;
  switch (actorType) {
    case 'ai_operator':     return <Bot      className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground', className)} />;
    case 'knowledge_persona': return <BookOpen className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground', className)} />;
    case 'human':
    default:                return <User     className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground', className)} />;
  }
}

// ─── Component ─────────────────────────────────────────────────────────────

export function ParticipantPicker({
  participants,
  onChange,
  actors,
  domains = [],
  disabled = false,
  loading = false,
  searchValue,
  onSearchChange,
  isSearching = false,
  className,
  'data-testid': testId,
}: ParticipantPickerProps): React.JSX.Element {
  const [localSearch, setLocalSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const effectiveSearch = searchValue ?? localSearch;

  const updateSearch = (next: string) => {
    if (searchValue === undefined) {
      setLocalSearch(next);
    }
    onSearchChange?.(next);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Already-added IDs to prevent duplicates
  const addedActorIds = new Set(participants.filter(p => p.actorId).map(p => p.actorId!));
  const addedDomainIds = new Set(participants.filter(p => p.domainId).map(p => p.domainId!));

  // AC-MUT2-5: thread actorType into picker options
  const allOptions: PickerOption[] = [
    ...actors
      .filter(a => !addedActorIds.has(a.id))
      .map(a => ({ kind: 'actor' as const, id: a.id, name: a.name, actorType: a.actorType })),
    ...domains
      .filter(d => !addedDomainIds.has(d.id))
      .map(d => ({ kind: 'domain' as const, id: d.id, name: d.name })),
  ];

  const filteredOptions = effectiveSearch.trim()
    ? allOptions.filter(o => o.name.toLowerCase().includes(effectiveSearch.toLowerCase()))
    : allOptions;

  const actorOptions = filteredOptions.filter(o => o.kind === 'actor');
  const domainOptions = filteredOptions.filter(o => o.kind === 'domain');

  const handleAdd = (option: PickerOption) => {
    const entry: ParticipantEntry = {
      key: nextKey(),
      participantKind: option.kind === 'actor' ? 'actor' : 'domain_accountable',
      actorId: option.kind === 'actor' ? option.id : null,
      domainId: option.kind === 'domain' ? option.id : null,
      // AC-MUT2-1: always default to 'attendee'; role pill removed
      role: 'attendee',
      displayName: option.name,
    };
    onChange([...participants, entry]);
    updateSearch('');
    setOpen(false);
  };

  const handleRemove = (key: string) => {
    onChange(participants.filter((p) => p.key !== key));
  };

  return (
    <div className={cn('space-y-2', className)} data-testid={testId}>
      {/* Participant list */}
      {loading && participants.length === 0 && (
        <p className="text-xs text-muted-foreground italic animate-pulse">{t('Loading participants...')}</p>
      )}
      {!loading && participants.length === 0 && (
        <p className="text-xs text-muted-foreground italic">{t('No participants yet.')}</p>
      )}

      {participants.map((p, idx) => {
        const isDomain = p.participantKind === 'domain_accountable';
        const domain = isDomain && p.domainId
          ? domains.find((d) => d.id === p.domainId)
          : null;
        const actor = !isDomain && p.actorId ? actors.find((a) => a.id === p.actorId) : null;
        const displayName = p.displayName
          ?? (actor ? actor.name : null)
          ?? (isDomain && domain ? domain.name : null)
          ?? (loading ? t('Loading...') : t('(unknown)'));

        return (
          <div
            key={p.key}
            className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-sm"
            data-testid={testId ? `${testId}-participant-${idx}` : undefined}
          >
            <div className="flex items-center gap-2 min-w-0">
              {/* AC-MUT2-5: type-specific icon */}
              <ActorTypeIcon
                actorType={actor?.actorType}
                isDomain={isDomain}
              />
              <span className="truncate text-sm">{displayName}</span>
              {/* AC-MUT2-1: role pill removed */}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-destructive"
              disabled={disabled}
              onClick={() => handleRemove(p.key)}
              aria-label={t('Remove participant')}
              data-testid={testId ? `${testId}-participant-${idx}-remove` : undefined}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        );
      })}

      {/* Searchable combobox to add participants */}
      {!disabled && (
        <div ref={containerRef} className="relative" data-testid={testId ? `${testId}-add` : undefined}>
          <Input
            type="text"
            value={effectiveSearch}
            onChange={(e) => {
              updateSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            // AC-MUT2-2: reopen list on click/pointer-down even after a selection
            // (after select, input keeps focus with open=false; this reopens it)
            onClick={() => setOpen(true)}
            placeholder={t('Search actors or domains...')}
            disabled={disabled}
            aria-label={t('Search actors or domains')}
            className="w-full text-sm"
          />

          {open && isSearching && (
            <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded-md shadow-md px-3 py-2 text-sm text-muted-foreground">
              {t('Loading...')}
            </div>
          )}

          {open && !isSearching && filteredOptions.length > 0 && (
            <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded-md shadow-md max-h-52 overflow-y-auto">
              {actorOptions.length > 0 && (
                <>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                    {t('Actors')}
                  </div>
                  {actorOptions.map(option => (
                    <Button
                      key={`actor-${option.id}`}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-none justify-start h-auto"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAdd(option);
                      }}
                    >
                      {/* AC-MUT2-5: type-specific icon in dropdown */}
                      <ActorTypeIcon actorType={option.actorType} />
                      <span>{option.name}</span>
                    </Button>
                  ))}
                </>
              )}

              {domainOptions.length > 0 && (
                <>
                  <div className={cn(
                    'px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border',
                    actorOptions.length > 0 && 'border-t'
                  )}>
                    {t('Domains')}
                  </div>
                  {domainOptions.map(option => (
                    <Button
                      key={`domain-${option.id}`}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-none justify-start h-auto"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAdd(option);
                      }}
                    >
                      <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span>{option.name}</span>
                    </Button>
                  ))}
                </>
              )}
            </div>
          )}

          {open && !isSearching && effectiveSearch.trim() !== '' && filteredOptions.length === 0 && (
            <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded-md shadow-md px-3 py-2 text-sm text-muted-foreground">
              {t('No results found.')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
